// src/ClinicalDataCollector/components/output/HEDISMetricsReport.jsx
import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Divider,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { useSelector } from 'react-redux';
import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

import { generateHedisMetricsFromAI } from '../../../../utils/services/hedisAiService';
import { buildHedisContextText } from '../../../../utils/clinicalNotesFormatter';

// Status chip con icono
const StatusChip = ({ status }) => {
  if (status === 'Closed') {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Closed"
        size="small"
        color="success"
        variant="filled"
      />
    );
  }
  if (status === 'Open') {
    return (
      <Chip
        icon={<WarningAmberIcon />}
        label="Open"
        size="small"
        color="warning"
        variant="outlined"
      />
    );
  }
  return (
    <Chip
      icon={<RemoveCircleOutlineIcon />}
      label="N/A"
      size="small"
      variant="outlined"
    />
  );
};

const HEDISMetricsReport = ({
  patientSummary,
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
}) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // AdvancedMD ID desde Redux
  const { advancedMdPatientId } = useSelector(
    (state) => state.patientMapSelect
  );

  // Contexto AdvancedMD para meds, labs, allergies
  const {
    medications,
    medicationsLoading,
    loadMedications,
    labs,
    labsLoading,
    loadLabs,
    allergies,
    allergiesLoading,
    loadAllergies,
  } = useContext(AdvancedMdContext);

  // Cargar meds / labs / allergies desde AdvancedMD
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadMedications(advancedMdPatientId);
    loadLabs(advancedMdPatientId);
    loadAllergies(advancedMdPatientId);
  }, [advancedMdPatientId, loadMedications, loadLabs, loadAllergies]);

  const anyBaseLoading = medicationsLoading || labsLoading || allergiesLoading;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const hedContextText = buildHedisContextText({
        demographics: patientSummary || {},
        annualWellnessNotes,
        followUpExamNotes,
        telehealthNotes,
        hospitalDischargeNotes,
        medications: Array.isArray(medications) ? medications : [],
        labs: Array.isArray(labs) ? labs : [],
        allergies: Array.isArray(allergies) ? allergies : [],
      });

      if (!hedContextText.trim()) {
        setError('No clinical data available for HEDIS evaluation.');
        setMetrics([]);
        return;
      }

      const aiMetrics = await generateHedisMetricsFromAI({ hedContextText });
      setMetrics(Array.isArray(aiMetrics) ? aiMetrics : []);
    } catch (err) {
      console.error('[HEDISMetricsReport] Error generating HEDIS:', err);
      setError('Error generating HEDIS metrics with AI.');
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
        <CardHeader
          title="HEDIS Metrics Report"
          subheader="AI-generated HEDIS quality measures based on patient clinical data"
          titleTypographyProps={{ variant: 'h5', align: 'center' }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                size="small"
                onClick={handleGenerate}
                disabled={loading || anyBaseLoading}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PsychologyIcon />
                  )
                }
              >
                {loading ? 'Generating...' : 'Generate HEDIS with AI'}
              </Button>

              <Tooltip title="View data sources for this output">
                <IconButton
                  size="small"
                  onClick={() => setInfoOpen(true)}
                  sx={{
                    bgcolor: 'warning.light',
                    '&:hover': { bgcolor: 'warning.main' },
                    color: 'common.black',
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {anyBaseLoading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading clinical data (medications, labs, allergies) from
              AdvancedMD...
            </Alert>
          )}

          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 3,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Generating HEDIS metrics with AI...
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && metrics.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No HEDIS data available yet. Click &quot;Generate HEDIS with
              AI&quot; to evaluate quality measures for this patient.
            </Typography>
          )}

          {!loading && !error && metrics.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Measure</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">Domain</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle2">Status</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        Last Evidence
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        Value / Target
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        Gap &amp; Recommendation
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics.map((m, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {m.measure || '—'}
                        </Typography>
                        {m.id && (
                          <Typography variant="caption" color="text.secondary">
                            {m.id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{m.domain || '—'}</TableCell>
                      <TableCell align="center">
                        <StatusChip status={m.status} />
                      </TableCell>
                      <TableCell>{m.lastDate || 'N/A'}</TableCell>
                      <TableCell>
                        {m.value || m.target
                          ? `${m.value || '—'} / ${m.target || '—'}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {m.gapRecommendation || m.reason || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info modal */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Input data used for this output</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Required clinical inputs
          </Typography>
          <ul>
            <li>
              <Typography variant="body2">
                <strong>Demographics:</strong> age, sex, payer.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Clinical notes:</strong> AWV, Follow-up, Telehealth,
                Hospital discharge (Assessment &amp; Plan, vitals, etc.).
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Blood pressure snapshots:</strong> systolic / diastolic
                readings documented in notes or vitals.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Laboratory results:</strong> A1c, lipids, kidney
                function, etc.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Medications:</strong> antihypertensives, statins,
                diabetes meds used as evidence of treatment.
              </Typography>
            </li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HEDISMetricsReport;
